import {
    CALCULATOR_SCREEN,
    SENDER_ADDITIONAL_INFO_SCREEN,
    PAYMENT_METHOD_SCREEN,
    RECIPIENT_MAIN_INFO_SCREEN,
    RECIPIENT_ADDITIONAL_INFO_SCREEN,
    CARD_EXTRA_FIELDS_SCREEN,
    SUB_SOURCES_SCREEN,
    TRANSFER_ADDITIONAL_INFO_SCREEN,
    HAS_CARD_EXTRA_FIELDS,
    HAS_SENDER_ADDITIONAL_FIELDS,
    HAS_RECIPIENT_ADDITIONAL_FIELDS,
    HAS_TRANSFER_ADDITIONAL_FIELDS,
    HAS_SUB_SOURCES,
    RECIPIENT_NOT_SAVED,
    DETERMINE_SCREEN_WITH_ERROR,
} from 'apps/auth/components/send/SendConstants';
import { enqueueActions, setup } from 'xstate';
import type { EventObject } from 'xstate';

type SendScreenContext = {
    hasSubSources?: boolean;
    recipientSaved?: boolean;
    isBonusWithdraw?: boolean;
    showCalculatorFirst?: boolean;
    hasCardExtraFields?: boolean;
    hasSenderAdditionalFields?: boolean;
    hasRecipientAdditionalFields?: boolean;
    hasTransferAdditionalFields?: boolean;
};

type DetermineErrorData = {
    hasErrorOnCalculator?: boolean;
    hasErrorOnRecipientMainFields?: boolean;
    hasErrorOnRecipientAdditionalFields?: boolean;
    hasErrorOnSenderAdditionalFields?: boolean;
    hasErrorOnTransferAdditionalFields?: boolean;
    hasErrorOnCardExtraFields?: boolean;
};

type ScreenEventNames =
    | typeof CALCULATOR_SCREEN
    | typeof RECIPIENT_MAIN_INFO_SCREEN
    | typeof SENDER_ADDITIONAL_INFO_SCREEN
    | typeof RECIPIENT_ADDITIONAL_INFO_SCREEN
    | typeof TRANSFER_ADDITIONAL_INFO_SCREEN
    | typeof PAYMENT_METHOD_SCREEN
    | typeof CARD_EXTRA_FIELDS_SCREEN
    | typeof SUB_SOURCES_SCREEN;

type ContextUpdateEvent = {
    type: 'NEXT_WITH_UPDATE_CONTEXT';
    data: Partial<SendScreenContext>;
};

type GoToScreenWithErrorEvent = {
    type: 'GO_TO_SCREEN_WITH_PRECHECK_ERROR';
    data?: DetermineErrorData;
};

type NavigationEvent = { type: 'NEXT' | 'BACK' | 'GO_TO_VIEW_RATES_FLOW' };

type ScreenNavigationEvent = { type: ScreenEventNames };

type SendScreenEvent = NavigationEvent | ContextUpdateEvent | GoToScreenWithErrorEvent | ScreenNavigationEvent;

const hasErrorPayload = (event: SendScreenEvent): event is GoToScreenWithErrorEvent => event.type === 'GO_TO_SCREEN_WITH_PRECHECK_ERROR';

const UPDATE_CONTEXT_AND_NEXT = enqueueActions<SendScreenContext, ContextUpdateEvent, undefined, SendScreenEvent, never, never, never, never, EventObject>(
    ({ enqueue, event }) => {
        enqueue.assign({ ...event.data });
        enqueue.raise({ type: 'NEXT' });
    },
);

const HAS_SUB_SOURCES_GUARD: 'hasSubSources' = HAS_SUB_SOURCES;
const RECIPIENT_NOT_SAVED_GUARD: 'recipientNotSaved' = RECIPIENT_NOT_SAVED;
const HAS_CARD_EXTRA_FIELDS_GUARD: 'hasCardExtraFields' = HAS_CARD_EXTRA_FIELDS;
const HAS_SENDER_ADDITIONAL_FIELDS_GUARD: 'hasSenderAdditionalFields' = HAS_SENDER_ADDITIONAL_FIELDS;
const HAS_RECIPIENT_ADDITIONAL_FIELDS_GUARD: 'hasRecipientAdditionalFields' = HAS_RECIPIENT_ADDITIONAL_FIELDS;
const HAS_TRANSFER_ADDITIONAL_FIELDS_GUARD: 'hasTransferAdditionalFields' = HAS_TRANSFER_ADDITIONAL_FIELDS;
const IS_BONUS_WITHDRAW_GUARD = 'isBonusWithdraw' as const;

const SUB_SOURCES_SCREEN_WITH_GUARD = { target: SUB_SOURCES_SCREEN, guard: HAS_SUB_SOURCES_GUARD };
const RECIPIENT_MAIN_INFO_SCREEN_WITH_GUARD = { target: RECIPIENT_MAIN_INFO_SCREEN, guard: RECIPIENT_NOT_SAVED_GUARD };
const CARD_EXTRA_FIELDS_SCREEN_WITH_GUARD = { target: CARD_EXTRA_FIELDS_SCREEN, guard: HAS_CARD_EXTRA_FIELDS_GUARD };
const SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD = { target: SENDER_ADDITIONAL_INFO_SCREEN, guard: HAS_SENDER_ADDITIONAL_FIELDS_GUARD };
const RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD = {
    target: RECIPIENT_ADDITIONAL_INFO_SCREEN,
    guard: HAS_RECIPIENT_ADDITIONAL_FIELDS_GUARD,
};
const TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD = {
    target: TRANSFER_ADDITIONAL_INFO_SCREEN,
    guard: HAS_TRANSFER_ADDITIONAL_FIELDS_GUARD,
};
const VIEW_RATES_FIRST_ENTRY_POINT = '#transfer.viewRatesFirst.CALCULATOR_SCREEN';
const RESET_TO_BONUS_FLOW = { target: '#transfer.bonusWithdrawFlow.RECIPIENT_MAIN_INFO_SCREEN', guard: IS_BONUS_WITHDRAW_GUARD };
const RESET_TO_DEFAULT_FLOW = '#transfer.defaultFlow.RECIPIENT_MAIN_INFO_SCREEN';
const COMPLETE_AND_RETURN_TO_BONUS_FLOW = { target: '#transfer.bonusWithdrawFlow.CALCULATOR_SCREEN', guard: IS_BONUS_WITHDRAW_GUARD };
const COMPLETE_AND_RETURN_TO_DEFAULT_FLOW = '#transfer.defaultFlow.CALCULATOR_SCREEN';

const BONUS_WITHDRAW_FLOW = 'bonusWithdrawFlow';

const DEFAULT_FLOW = 'defaultFlow';

const SHOW_CALCULATOR_FIRST_FLOW = 'showCalculatorFirstFlow';
const PRECHECK_SCREEN_USUAL_FLOW = { target: PAYMENT_METHOD_SCREEN };
const PRECHECK_SCREEN_BONUS_FLOW = { target: CALCULATOR_SCREEN };

const getInitialState = (ctx: SendScreenContext) => {
    if (ctx.isBonusWithdraw) {
        return BONUS_WITHDRAW_FLOW;
    }
    if (ctx.showCalculatorFirst) {
        return SHOW_CALCULATOR_FIRST_FLOW;
    }
    return DEFAULT_FLOW;
};
/**
 * Что такое XState?
 * XState — это библиотека для управления состоянием в JavaScript-приложениях.
 *
 * 🔹 Как это работает?
 * Вместо множества if-else и switch-case XState позволяет чётко описать:
 *   ✅ Какие состояния существуют (например, recipient, amount, confirmation)
 *   ✅ Как они связаны между собой (что идёт после чего)
 *   ✅ При каких условиях возможны переходы между состояниями
 *
 * 🔹 Зачем это нужно?
 * XState помогает управлять сложными сценариями переключения экранов, шагов или процессов.
 * Можно выделить родительский state и разделить логику для разных сценариев, например:
 *   - Повторный платёж (repeat)
 *   - Незавершённые транзакции (incomplete)
 *   - Оплата бонусами (bonus payment)
 *   - обычное флоу
 *
 * Это даёт несколько преимуществ:
 *   ✅ Вся логика находится в одном месте — её проще понять и поддерживать
 *   ✅ Чёткая структура переходов — легко добавить новые экраны и связи между ними
 *   ✅ Уменьшается дублирование кода — нет необходимости описывать логику переходов для каждого экрана отдельно
 */
export const sendScreenMachine = (firstScreen: string, initialContext: SendScreenContext) =>
    setup({
        types: {
            context: {} as SendScreenContext,
            events: {} as SendScreenEvent,
        },
        guards: {
            [HAS_SUB_SOURCES_GUARD]: () => Boolean(initialContext.hasSubSources),
            [RECIPIENT_NOT_SAVED_GUARD]: () => !initialContext.recipientSaved,
            [IS_BONUS_WITHDRAW_GUARD]: () => Boolean(initialContext.isBonusWithdraw),
            [HAS_CARD_EXTRA_FIELDS_GUARD]: ({ context }) => Boolean(context.hasCardExtraFields),
            [HAS_RECIPIENT_ADDITIONAL_FIELDS_GUARD]: ({ context }) => Boolean(context.hasRecipientAdditionalFields),
            [HAS_SENDER_ADDITIONAL_FIELDS_GUARD]: ({ context }) => Boolean(context.hasSenderAdditionalFields),
            [HAS_TRANSFER_ADDITIONAL_FIELDS_GUARD]: ({ context }) => Boolean(context.hasTransferAdditionalFields),
        },
    }).createMachine({
        id: 'transfer',
        initial: getInitialState(initialContext),
        context: initialContext,
        states: {
            defaultFlow: {
                initial: firstScreen,
                states: {
                    SUB_SOURCES_SCREEN: {
                        on: {
                            NEXT: RECIPIENT_MAIN_INFO_SCREEN,
                        },
                    },
                    RECIPIENT_MAIN_INFO_SCREEN: {
                        on: {
                            NEXT: CALCULATOR_SCREEN,
                            BACK: SUB_SOURCES_SCREEN_WITH_GUARD,
                            GO_TO_VIEW_RATES_FLOW: VIEW_RATES_FIRST_ENTRY_POINT,
                        },
                    },
                    CALCULATOR_SCREEN: {
                        on: {
                            NEXT: PAYMENT_METHOD_SCREEN,
                            BACK: RECIPIENT_MAIN_INFO_SCREEN_WITH_GUARD,
                        },
                    },
                    PAYMENT_METHOD_SCREEN: {
                        on: {
                            NEXT_WITH_UPDATE_CONTEXT: {
                                actions: UPDATE_CONTEXT_AND_NEXT,
                            },

                            NEXT: [
                                CARD_EXTRA_FIELDS_SCREEN_WITH_GUARD,
                                SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                                RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                                TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                            ],
                            BACK: CALCULATOR_SCREEN,
                            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
                        },
                    },
                    ...ADDITIONAL_SCREENS_DEFAULT_FLOW,
                },
            },
            viewRatesFirst: {
                initial: CALCULATOR_SCREEN,
                states: {
                    CALCULATOR_SCREEN: {
                        on: {
                            NEXT: RECIPIENT_MAIN_INFO_SCREEN,
                            BACK: [RESET_TO_BONUS_FLOW, RESET_TO_DEFAULT_FLOW],
                        },
                    },
                    RECIPIENT_MAIN_INFO_SCREEN: {
                        on: {
                            NEXT: [COMPLETE_AND_RETURN_TO_BONUS_FLOW, COMPLETE_AND_RETURN_TO_DEFAULT_FLOW],
                            BACK: CALCULATOR_SCREEN,
                        },
                    },
                },
            },
            showCalculatorFirstFlow: {
                initial: firstScreen,
                states: {
                    SUB_SOURCES_SCREEN: {
                        on: {
                            NEXT: CALCULATOR_SCREEN,
                        },
                    },
                    CALCULATOR_SCREEN: {
                        on: {
                            NEXT: RECIPIENT_MAIN_INFO_SCREEN,
                            BACK: SUB_SOURCES_SCREEN_WITH_GUARD,
                        },
                    },
                    RECIPIENT_MAIN_INFO_SCREEN: {
                        on: {
                            NEXT: PAYMENT_METHOD_SCREEN,
                            BACK: CALCULATOR_SCREEN,
                        },
                    },
                    PAYMENT_METHOD_SCREEN: {
                        on: {
                            NEXT_WITH_UPDATE_CONTEXT: {
                                actions: UPDATE_CONTEXT_AND_NEXT,
                            },
                            NEXT: [
                                CARD_EXTRA_FIELDS_SCREEN_WITH_GUARD,
                                SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                                RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                                TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                            ],
                            BACK: RECIPIENT_MAIN_INFO_SCREEN,
                            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
                        },
                    },
                    ...ADDITIONAL_SCREENS_DEFAULT_FLOW,
                    [DETERMINE_SCREEN_WITH_ERROR]: DETERMINE_SCREEN_WITH_ERROR_STATE,
                },
            },
            bonusWithdrawFlow: {
                initial: firstScreen,
                states: {
                    SUB_SOURCES_SCREEN: {
                        on: {
                            NEXT: RECIPIENT_MAIN_INFO_SCREEN,
                        },
                    },
                    RECIPIENT_MAIN_INFO_SCREEN: {
                        on: {
                            NEXT: CALCULATOR_SCREEN,
                            BACK: SUB_SOURCES_SCREEN_WITH_GUARD,
                            GO_TO_VIEW_RATES_FLOW: VIEW_RATES_FIRST_ENTRY_POINT,
                        },
                    },
                    CALCULATOR_SCREEN: {
                        on: {
                            NEXT: [
                                SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                                RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                                TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                            ],
                            BACK: RECIPIENT_MAIN_INFO_SCREEN_WITH_GUARD,
                            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
                        },
                    },
                    SENDER_ADDITIONAL_INFO_SCREEN: {
                        on: {
                            NEXT: [RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD, TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, PRECHECK_SCREEN_BONUS_FLOW],
                            BACK: [CALCULATOR_SCREEN],
                            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
                        },
                    },
                    RECIPIENT_ADDITIONAL_INFO_SCREEN: {
                        on: {
                            NEXT: [TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, PRECHECK_SCREEN_BONUS_FLOW],
                            BACK: [SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, CALCULATOR_SCREEN],
                            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
                        },
                    },
                    TRANSFER_ADDITIONAL_INFO_SCREEN: {
                        on: {
                            NEXT: [PRECHECK_SCREEN_BONUS_FLOW],
                            BACK: [RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD, SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, CALCULATOR_SCREEN],
                            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
                        },
                    },
                    DETERMINE_SCREEN_WITH_ERROR: {
                        entry: enqueueActions(({ enqueue, event }) => {
                            const typedEvent = event as SendScreenEvent;
                            const targetScreen = determineErrorScreen(hasErrorPayload(typedEvent) ? typedEvent.data : undefined);
                            enqueue.raise({ type: targetScreen } as SendScreenEvent);
                        }),
                        on: {
                            [RECIPIENT_MAIN_INFO_SCREEN]: RECIPIENT_MAIN_INFO_SCREEN,
                            [CALCULATOR_SCREEN]: CALCULATOR_SCREEN,
                            [SENDER_ADDITIONAL_INFO_SCREEN]: SENDER_ADDITIONAL_INFO_SCREEN,
                            [RECIPIENT_ADDITIONAL_INFO_SCREEN]: RECIPIENT_ADDITIONAL_INFO_SCREEN,
                            [TRANSFER_ADDITIONAL_INFO_SCREEN]: TRANSFER_ADDITIONAL_INFO_SCREEN,
                        },
                    },
                },
            },
        },
    });

/**
 * View rates flow for March 2025
 * 1. View rates button immediately opens the calculator
 * 2. After the calculator we again push the step of filling in the recipient's data (without the View Rates First button)
 * 3. If a step with recipient's data appears for the second time (point 2), the previous copy is deleted from the stack
 * 4. If the user goes back - it will return to the calculator and then to the choice of delivery method
 * 5. If the user goes forward, the calculator will open again, and delete the previous copy of the calculator from the stack
 * 6. As a result - before selecting the payment option or before showing the confirmation bottom sheet - there will always be only a calculator
 * 7. The previous screen copy is deleted from the stack only when the new one is shown (edited)
 */

// ============================================================================
// State Constants
// ============================================================================

// Constant for DETERMINE_SCREEN_WITH_ERROR state with entry and on blocks
const DETERMINE_SCREEN_WITH_ERROR_STATE = {
    entry: enqueueActions<SendScreenContext, SendScreenEvent, undefined, SendScreenEvent, never, never, never, never, EventObject>(({ enqueue, event }) => {
        const targetScreen = determineErrorScreen(hasErrorPayload(event) ? event.data : undefined);
        enqueue.raise({ type: targetScreen });
    }),
    on: {
        [RECIPIENT_MAIN_INFO_SCREEN]: RECIPIENT_MAIN_INFO_SCREEN,
        [CALCULATOR_SCREEN]: CALCULATOR_SCREEN,
        [CARD_EXTRA_FIELDS_SCREEN]: CARD_EXTRA_FIELDS_SCREEN,
        [SENDER_ADDITIONAL_INFO_SCREEN]: SENDER_ADDITIONAL_INFO_SCREEN,
        [RECIPIENT_ADDITIONAL_INFO_SCREEN]: RECIPIENT_ADDITIONAL_INFO_SCREEN,
        [TRANSFER_ADDITIONAL_INFO_SCREEN]: TRANSFER_ADDITIONAL_INFO_SCREEN,
    },
};

// Constant for additional screens states in defaultFlow
const ADDITIONAL_SCREENS_DEFAULT_FLOW = {
    // добавление новой карты
    [CARD_EXTRA_FIELDS_SCREEN]: {
        on: {
            NEXT_WITH_UPDATE_CONTEXT: {
                actions: UPDATE_CONTEXT_AND_NEXT,
            },
            NEXT: [
                SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                PRECHECK_SCREEN_USUAL_FLOW,
            ],
            BACK: PAYMENT_METHOD_SCREEN,
            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
        },
    },
    [SENDER_ADDITIONAL_INFO_SCREEN]: {
        on: {
            NEXT: [RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD, TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, PRECHECK_SCREEN_USUAL_FLOW],
            BACK: [CARD_EXTRA_FIELDS_SCREEN_WITH_GUARD, PAYMENT_METHOD_SCREEN],
            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
        },
    },
    [RECIPIENT_ADDITIONAL_INFO_SCREEN]: {
        on: {
            NEXT: [TRANSFER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, PRECHECK_SCREEN_USUAL_FLOW],
            BACK: [SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD, CARD_EXTRA_FIELDS_SCREEN_WITH_GUARD, PAYMENT_METHOD_SCREEN],
            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
        },
    },
    [TRANSFER_ADDITIONAL_INFO_SCREEN]: {
        on: {
            NEXT: PRECHECK_SCREEN_USUAL_FLOW,
            BACK: [
                RECIPIENT_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                SENDER_ADDITIONAL_INFO_SCREEN_WITH_GUARD,
                CARD_EXTRA_FIELDS_SCREEN_WITH_GUARD,
                PAYMENT_METHOD_SCREEN,
            ],
            GO_TO_SCREEN_WITH_PRECHECK_ERROR: DETERMINE_SCREEN_WITH_ERROR,
        },
    },
    [DETERMINE_SCREEN_WITH_ERROR]: DETERMINE_SCREEN_WITH_ERROR_STATE,
};

function determineErrorScreen(ctx?: DetermineErrorData): ScreenEventNames {
    const {
        hasErrorOnCalculator,
        hasErrorOnRecipientMainFields,
        hasErrorOnRecipientAdditionalFields,
        hasErrorOnSenderAdditionalFields,
        hasErrorOnTransferAdditionalFields,
        hasErrorOnCardExtraFields,
    } = ctx ?? {};

    if (hasErrorOnCalculator) {
        return CALCULATOR_SCREEN;
    } else if (hasErrorOnRecipientMainFields) {
        return RECIPIENT_MAIN_INFO_SCREEN;
    } else if (hasErrorOnRecipientAdditionalFields) {
        return RECIPIENT_ADDITIONAL_INFO_SCREEN;
    } else if (hasErrorOnCardExtraFields) {
        return CARD_EXTRA_FIELDS_SCREEN;
    } else if (hasErrorOnSenderAdditionalFields) {
        return SENDER_ADDITIONAL_INFO_SCREEN;
    } else if (hasErrorOnTransferAdditionalFields) {
        return TRANSFER_ADDITIONAL_INFO_SCREEN;
    } else {
        return PAYMENT_METHOD_SCREEN;
    }
}
